import { BrandLogo, Flex } from '@brightdrop/bd-ui';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useTranslations } from '@brightdrop/localization-client';
import { cx } from '@emotion/css';
import {
  useEntitlements,
  usePlanManagementSurfaceEnablement,
} from '@gm-commercial/entitlements-client';
import { useProfile, useRole } from '@gm-commercial/profile-context';
import { Role } from '@gm-commercial/profile-model';
import { Divider, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer, { type DrawerProps } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import {
  type CSSObject,
  styled,
  type Theme,
  useTheme,
} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createSelector } from '@reduxjs/toolkit';
import { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { useAppDispatch } from '~/app/store';
import CloseIcon from '~/assets/icons/new/close_clearInput.svg?react';
import MenuIcon from '~/assets/icons/new/menu.svg?react';
import GMLogo from '~/assets/images/gm-logo.svg?react';
import { SecureWrapper } from '~/common/components';
import { BackToHomeLink } from '~/common/components/BackToHomeLink/BackToHomeLink';
import { useCamerasSettingsAvailability } from '~/common/hooks/useCamerasSettingsAvailability';
import useFleetProgramsAvailability from '~/common/hooks/useFleetProgramsAvailability';
import useFleetSwitcherVisibility from '~/common/hooks/useFleetSwitcherVisibility';
import useSessionId from '~/common/hooks/useSessionId';
import {
  CUSTOM_VIDEO_REQUEST_FLAG,
  FLEET_MAP_FLAG,
  ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG,
} from '~/common/models/featureFlags.model';
import { ProductCodes } from '~/common/models/plans.model';
import {
  findRouteFromPath,
  getDefaultRoutePath,
} from '~/common/utils/route/route.utils';
import { useLinkedPartners } from '~/features/integrations/hooks/useLinkedPartners';
import { isNavListItem } from '~/features/navigationDrawer/config/legacy.navDrawer.config';
import {
  getAppNameFromPath,
  getNavDrawerConfig,
  isAccountAreaAppName,
} from '~/features/navigationDrawer/config/navDrawer.config';
import { sanitizeNavDrawerConfig } from '~/features/navigationDrawer/config/sanitizeNavDrawerConfig';
import { useSafetyAccess } from '~/routes/organizations/safety/hooks/useSafetyAccess';
import { useEmerNotificationsAccess } from '~/routes/settings/hooks/useEmerNotificationsAccess';
import {
  selectActingPermissionsContextIds,
  selectActingProfileFirstName,
  selectActingProfileId,
  selectActingProfileLastName,
  selectActingProfileOrganization,
  selectActingProfileStatus,
  selectActingRole,
  selectProfileUserPreferences,
} from '~/store/profile/profileSlice.selectors';
import CloseDrawerIcon from '../../assets/icons/closeDrawerIcon.svg?react';
import { useShopAddOnsEnablement } from '../catalog/hooks/useShopAddOnsEnablement';
import { getFleetSwitcherSupportedRoutes } from '../fleets/switcher/configs/fleetSwitcher.config';
import FleetSwitcherContainer from '../fleets/switcher/FleetSwitcherContainer';
import { HEADER_HEIGHT } from '../header/GlobalHeader.styles';
import { useShopAddOnsPlanManagementConfig } from '../plans/hooks/useShopAddOnsPlanManagementConfig';
import FeedbackNavItem from './FeedbackNavItem';
import { resolveShowTelematicsFleetNavSurfaces } from './fleetTelematicsNavSurfaces.utils';
import { NAV_DRAWER_MESSAGES } from './GlobalNavDrawer.messages';
import useStyles, { NAV_DRAWER_OPEN_WIDTH } from './GlobalNavDrawer.styles';
import NavDrawerItem from './NavDrawerItem';
import OnStarNavRibbon from './OnStarNavRibbon';

export type FeedbackNavAction = {
  onOpen: () => void;
};

// Exported for use in global wrapper to ensure proper spacing of content below header
/**
 * Used to
 *
 * Exported for use in global wrapper to ensure proper spacing of content below header.
 *
 * @see https://v5.mui.com/material-ui/react-app-bar/#fixed-placement
 */
export const DrawerHeader = styled('div')(({ theme }) => ({
  zIndex: theme.zIndex.drawer,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  [theme.breakpoints.down('sm')]: {
    minHeight: `${HEADER_HEIGHT}px`,
  },
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const openedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  width: `${NAV_DRAWER_OPEN_WIDTH}px`,
});

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => !['open'].includes(prop as string),
})<DrawerProps>(({ theme, open }) => ({
  width: NAV_DRAWER_OPEN_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',

  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
}));

const selectActingProfileParams = createSelector(
  selectActingProfileId,
  selectActingProfileFirstName,
  selectActingProfileLastName,
  selectActingRole,
  selectActingProfileStatus,
  selectActingProfileOrganization,
  selectActingPermissionsContextIds,
  (usersId, firstName, lastName, role, status, organization, contextIds) => {
    return {
      usersId,
      firstName,
      lastName,
      role,
      status,
      organization,
      ...contextIds,
    };
  }
);

const GlobalNavDrawer = ({
  navExpanded,
  toggleDrawer,
  onNavItemClick,
  toggleable = true,
  feedbackNavAction,
  isMinimal = false,
}: {
  navExpanded: boolean;
  toggleDrawer: () => void;
  onNavItemClick?: () => void;
  toggleable?: boolean;
  feedbackNavAction?: FeedbackNavAction;
  isMinimal?: boolean;
}) => {
  const { profile, actingProfile } = useProfile();
  const effectiveProfile = actingProfile ?? profile;
  const { getFlag } = useFeatureFlags();
  const isFleetMapFlagEnabled = getFlag(FLEET_MAP_FLAG);
  const { canShowPlanManagementByEnablement } =
    usePlanManagementSurfaceEnablement();
  const { classes } = useStyles({
    isMinimal,
  });
  const { isFleetSwitcherVisible } = useFleetSwitcherVisibility();
  const { isShopAddOnsEnabled } = useShopAddOnsPlanManagementConfig();
  const { hasProduct } = useEntitlements();
  const hasTelematicsProductPlan = hasProduct(ProductCodes.OST);
  const hasOneFleetProductPlan = hasProduct(ProductCodes.OSOF);
  const { hasEmerAccess } = useEmerNotificationsAccess();
  const { isProtectOnly } = useSafetyAccess();
  const { isHardwareEnabledInMarketplace } = useShopAddOnsEnablement();
  const { isAvailable: showFleetPrograms } = useFleetProgramsAvailability();

  const showTelematicsFleetNavSurfaces = useMemo(
    () =>
      resolveShowTelematicsFleetNavSurfaces({
        getFlag,
        hasTelematicsProductPlan:
          hasTelematicsProductPlan || hasOneFleetProductPlan,
      }),
    [getFlag, hasTelematicsProductPlan, hasOneFleetProductPlan]
  );

  // Camera availability is async: a failed camera-settings fetch means the org has
  // no cameras, so the Video requests nav item stays hidden. Fetch only when that
  // item could show at all, mirroring the consolidated Settings page.
  const isCustomVideoRequestEnabled = Boolean(
    getFlag(CUSTOM_VIDEO_REQUEST_FLAG)
  );
  const { showCameraSurfaces } = useCamerasSettingsAvailability({
    enabled: isCustomVideoRequestEnabled && showTelematicsFleetNavSurfaces,
  });

  const { translations } = useTranslations(NAV_DRAWER_MESSAGES);
  const theme = useTheme();
  const isViewportBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  const isViewportBelowLg = useMediaQuery(theme.breakpoints.down('lg'));
  const actingProfileParams = useSelector(selectActingProfileParams);
  const userPreferences = useSelector(selectProfileUserPreferences);
  const { pathname } = useLocation();

  const appName = getAppNameFromPath(pathname);
  const { hasRole } = useRole();
  const { linkedPartners } = useLinkedPartners({
    enabled: isAccountAreaAppName(appName) && hasRole([Role.ORG_MANAGER]),
  });
  const showIntegrations = linkedPartners.length > 0;
  const dispatch = useAppDispatch();

  const sessionId = useSessionId(true, false);
  const defaultRoutePath = useMemo(
    () =>
      actingProfileParams?.role
        ? getDefaultRoutePath(
            actingProfileParams?.role,
            {
              organizationsId: actingProfileParams?.organizationsId,
            },
            effectiveProfile
          )
        : '/',
    [actingProfileParams, effectiveProfile]
  );

  const navConfig = useMemo(
    () =>
      sanitizeNavDrawerConfig(
        getNavDrawerConfig({
          messages: translations,
          profileParams: actingProfileParams,
          hasRole,
          isFleetMapFlagEnabled: !!isFleetMapFlagEnabled,
          appName,
          sessionId,
          dispatch,
          findFlagScope: getFlag,
          canShowPlanManagementByEnablement,
          showTelematicsFleetNavSurfaces,
          hasEmerNotificationsAccess: hasEmerAccess,
          isProtectOnly,
          isShopAddOnsEnabled,
          showGettingStarted: !!userPreferences?.showGettingStarted,
          isHardwareEnabledInMarketplace,
          showCameraNavSurfaces: showCameraSurfaces,
          showFleetPrograms,
          showIntegrations,
        })
      ),
    [
      translations,
      actingProfileParams,
      hasRole,
      appName,
      sessionId,
      dispatch,
      getFlag,
      isFleetMapFlagEnabled,
      canShowPlanManagementByEnablement,
      showTelematicsFleetNavSurfaces,
      hasEmerAccess,
      isProtectOnly,
      isShopAddOnsEnabled,
      userPreferences?.showGettingStarted,
      isHardwareEnabledInMarketplace,
      showCameraSurfaces,
      showFleetPrograms,
      showIntegrations,
    ]
  );

  const isFleetSwitcherDisabled = !getFleetSwitcherSupportedRoutes(
    Boolean(actingProfileParams.organizationsId)
  ).some((path) => matchPath(path, pathname));

  const isAccountArea = isAccountAreaAppName(appName);

  const drawerPresentationOpen = isMinimal ? false : navExpanded;
  const isOnStarFleetIntelligenceRebrandEnabled = Boolean(
    getFlag(ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG)
  );

  const shouldRenderDrawer = isMinimal || (navConfig.items?.length ?? 0) > 0;

  return (
    <>
      {shouldRenderDrawer && (
        <StyledDrawer
          id="nav-drawer-menu"
          variant="permanent"
          open={drawerPresentationOpen}
          className={cx(classes.drawer, {
            [classes.drawerOpen]: !!drawerPresentationOpen,
            [classes.drawerClose]: !drawerPresentationOpen,
          })}
          classes={{
            paper: cx({
              [classes.drawerOpen]: !!drawerPresentationOpen,
              [classes.drawerClose]: !drawerPresentationOpen,
            }),
          }}
        >
          <DrawerHeader className={classes.logoContainer}>
            {!isMinimal && (
              <IconButton
                className={cx('menu', { isOpen: navExpanded })}
                onClick={toggleDrawer}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Flex>
              <Link to={defaultRoutePath || '/'}>
                <Box
                  className={cx('appLogo', { isOpen: drawerPresentationOpen })}
                  data-image-key="app-logo"
                  data-testid="app-logo"
                  id="app-logo"
                >
                  {!drawerPresentationOpen &&
                  !isOnStarFleetIntelligenceRebrandEnabled ? (
                    <GMLogo
                      width={32}
                      height={32}
                      role="img"
                      aria-label={
                        translations.legacyMenu['common:navDrawer.gmFleet']
                      }
                      data-testid="gm-logo-monogram"
                    />
                  ) : (
                    <BrandLogo
                      width={
                        drawerPresentationOpen
                          ? isOnStarFleetIntelligenceRebrandEnabled
                            ? 231
                            : 106
                          : 32
                      }
                      height={32}
                      isNavCollapsed={!drawerPresentationOpen}
                      data-testid={
                        drawerPresentationOpen
                          ? undefined
                          : 'onstar-logo-monogram'
                      }
                    />
                  )}
                </Box>
              </Link>
            </Flex>
            {!isMinimal && (
              <IconButton
                className={cx('toggle', { isOpen: navExpanded })}
                onClick={toggleDrawer}
              >
                <CloseIcon
                  width={isOnStarFleetIntelligenceRebrandEnabled ? 16 : 24}
                  height={isOnStarFleetIntelligenceRebrandEnabled ? 16 : 24}
                />
              </IconButton>
            )}
          </DrawerHeader>
          {!isMinimal && (
            <>
              {isFleetSwitcherVisible && (
                <>
                  <FleetSwitcherContainer
                    navExpanded={navExpanded}
                    disabled={isFleetSwitcherDisabled}
                  />
                  <Divider className={classes.divider} />
                </>
              )}
              {isAccountArea && (
                <Box
                  className={cx(classes.backToLinkContainer, {
                    isOpen: navExpanded,
                  })}
                  data-testid="nav-back-to-link"
                >
                  <BackToHomeLink to={defaultRoutePath || '/'} />
                </Box>
              )}
              {navConfig.title && (
                <Box
                  className={cx(classes.titleContainer, {
                    isOpen: navExpanded,
                  })}
                  data-testid="nav-title"
                >
                  <Typography variant="header1_medium">
                    {navConfig.title}
                  </Typography>
                </Box>
              )}
              <Box className={classes.drawerBody}>
                <List className={classes.navDrawerList}>
                  {navConfig.items.map((item) => {
                    const matchingRouteConfig = findRouteFromPath(item.path);
                    const drawerItem = (
                      <Fragment key={`menu-item-${item.testId}`}>
                        <NavDrawerItem
                          {...item}
                          isNavDrawerOpen={navExpanded}
                          callback={() => {
                            if (item.callback) {
                              item.callback();
                            }
                            if (onNavItemClick) {
                              onNavItemClick();
                            }
                          }}
                        />
                        {item.bordered && (
                          <Divider
                            className={classes.itemDivider}
                            data-testid={`nav-item-border-divider-${item.testId}`}
                          />
                        )}
                      </Fragment>
                    );

                    // SecureWrapper is used to control visibility for all nav links
                    if (isNavListItem(item)) {
                      return (
                        <SecureWrapper
                          requiredPermissions={item.permissions}
                          excludedRoles={item.excludedRoles}
                          requiredRoles={item.requiredRoles}
                          key={item.testId}
                        >
                          {drawerItem}
                        </SecureWrapper>
                      );
                    }
                    if (matchingRouteConfig) {
                      return (
                        <SecureWrapper
                          requiredPermissions={
                            matchingRouteConfig.authorizedPerms
                          }
                          excludedRoles={matchingRouteConfig.excludedRoles}
                          key={item.testId}
                        >
                          {drawerItem}
                        </SecureWrapper>
                      );
                    }
                    return drawerItem;
                  })}
                </List>
                {/* Below md, open drawer hides `drawerToggle`; render feedback in-body (same divider + row). */}
                {toggleable &&
                  feedbackNavAction &&
                  navExpanded &&
                  isViewportBelowMd && (
                    <Box className={classes.drawerFooterStandalone}>
                      <Divider className={classes.divider} />
                      <FeedbackNavItem
                        isNavDrawerOpen={navExpanded}
                        onOpen={feedbackNavAction.onOpen}
                      />
                    </Box>
                  )}
                {!toggleable && (
                  <Box className={classes.drawerFooterStandalone}>
                    <Divider className={classes.divider} />
                    {!isViewportBelowLg && (
                      <OnStarNavRibbon isNavDrawerOpen={navExpanded} />
                    )}
                    {feedbackNavAction && (
                      <FeedbackNavItem
                        isNavDrawerOpen={navExpanded}
                        onOpen={feedbackNavAction.onOpen}
                      />
                    )}
                  </Box>
                )}
              </Box>
              {toggleable && (
                <Box
                  className={cx(classes.drawerToggle, { isOpen: navExpanded })}
                >
                  <Divider className={classes.divider} />
                  {!isViewportBelowLg && (
                    <OnStarNavRibbon isNavDrawerOpen={navExpanded} />
                  )}
                  {feedbackNavAction ? (
                    <Stack
                      direction={navExpanded ? 'row' : 'column'}
                      alignItems={navExpanded ? 'center' : 'stretch'}
                      justifyContent={
                        navExpanded ? 'space-between' : 'flex-start'
                      }
                      spacing={0}
                      className={cx(
                        classes.drawerBottomSplit,
                        !navExpanded && classes.drawerBottomSplitMinimized
                      )}
                    >
                      {!(isViewportBelowMd && navExpanded) && (
                        <Box
                          className={cx(
                            classes.drawerFeedbackGrow,
                            !navExpanded && classes.drawerFeedbackRailCollapsed
                          )}
                        >
                          <FeedbackNavItem
                            isNavDrawerOpen={navExpanded}
                            onOpen={feedbackNavAction.onOpen}
                          />
                        </Box>
                      )}
                      <Box
                        className={cx(
                          classes.drawerCollapseCell,
                          !navExpanded && classes.drawerCollapseRailCollapsed
                        )}
                      >
                        <IconButton
                          onClick={toggleDrawer}
                          id={
                            navExpanded
                              ? 'nav-bar-close-btn'
                              : 'nav-bar-open-btn'
                          }
                          data-testid={
                            navExpanded
                              ? 'nav-bar-close-btn'
                              : 'nav-bar-open-btn'
                          }
                        >
                          <CloseDrawerIcon />
                        </IconButton>
                      </Box>
                    </Stack>
                  ) : (
                    <Box className={classes.drawerCollapseOnlyWrap}>
                      <IconButton
                        onClick={toggleDrawer}
                        id={
                          navExpanded ? 'nav-bar-close-btn' : 'nav-bar-open-btn'
                        }
                        data-testid={
                          navExpanded ? 'nav-bar-close-btn' : 'nav-bar-open-btn'
                        }
                      >
                        <CloseDrawerIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </StyledDrawer>
      )}
    </>
  );
};

export default GlobalNavDrawer;
